import {
  createContext,
  useEffect,
  useLayoutEffect,
  useState
} from 'react'
import i18n from 'i18n'

import { useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'

import { useSnackbar } from 'hooks'
import { useGetMe } from 'api/user'
import { useLogin, useLoginExternal } from 'api/auth'

import { STATE_VERSION, USER_STATUS } from 'config/constants'
import { capitalize } from 'utils/common'
import { useTranslation } from 'react-i18next'
import { ReactComponent as ErrorSVG } from 'assets/icons/snackbar/error.svg'


export const sessionContext = createContext({})
sessionContext.displayName = 'SessionContext'


const SessionProvider = ({ children }) => {

  const { t } = useTranslation()
  const { Snackbar } = useSnackbar('error')


  const [initApp, setInitApp] = useState(
    () => window.localStorage.getItem('state') === STATE_VERSION
  )

  const [secret, setSecret] = useState(
    () => window.localStorage.getItem('secret') || ''
  )

  const [user, setUser] = useState(
    () => JSON.parse(window.localStorage.getItem('user'))
  )

  const isReadyToLogIn = initApp && !!secret && !!user?.profile

  const [isLoggedIn, setIsLoggedIn] = useState(isReadyToLogIn)
  const [isPreLoggedIn, setIsPreLoggedIn] = useState(false)
  
  const [isLogging, setIsLogging] = useState(false)

  const queryClient = useQueryClient()
  const dispatch = useDispatch()

  const {
    login,
    data: loginData,
    error: loginError,
    reset: resetLogin,
  } = useLogin()

  const {
    loginExternal,
    data: loginExtData,
    error: loginExtError,
    reset: resetLoginExt
  } = useLoginExternal()

  const { data: meData } = useGetMe()

  const handleLogin = async ({ email, password } = {}) => {
    setIsLogging(true)

    if (isPreLoggedIn) {
      /**
       * User pre-logged means that we have all data
       * but user is not logged, so we just invalidate /me
       * in order to update user.status and check it again.
       */
      queryClient.invalidateQueries('me')
      return
    }

    login({ email, password })
  }

  const loginWithSecret = (newSecret) => {
    setIsLogging(true)
    setSecret(newSecret)
  }

  const handleLoginExternal = ({ provider, token }) => {
    setIsLogging(true)
    loginExternal({ provider, token })
  }

  const internalLogin = () => {

    if (!user.status.isConfirmed) {
      setIsPreLoggedIn(true)

    } else {
      setIsLoggedIn(true)
      window.localStorage.setItem('user', JSON.stringify(user))
      window.localStorage.setItem('secret', secret)
    }

    setIsLogging(false)
  }

  const logout = () => {
    queryClient.removeQueries(['user'])
    localStorage.removeItem('secret')
    localStorage.removeItem('user')
    resetLogin()
    resetLoginExt()
    setSecret('')
    setUser(null)
    setIsLogging(false)
    setIsLoggedIn(false)
    setIsPreLoggedIn(false)
    dispatch({ type: 'LOGOUT' })
  }

  const getStatuses = () => {
    const statuses = Object.entries(USER_STATUS).map(([key, value]) => {
      const statusName = `is${capitalize(key, false)}`
      const statusValue = !!(meData.status & value) // bitwise operation
      return ({ [statusName]: statusValue })
    })

    return Object.assign({}, ...statuses );
  }


  /**
   * STEP 1: CHECK VERSION
   * In case STATE VERSION and CLIENT VERSION
   * are differents, we need to force a logout.
   */
  useLayoutEffect(() => {
    if (initApp) return

    window.localStorage.setItem('state', STATE_VERSION)
    logout()
    setInitApp(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * STEP 2: LOGIN DATA
   * This runs after the login executed by the user
   */
  useEffect(() => {
    if (!loginData && !loginExtData) return;

    const data = loginData || loginExtData

    setSecret(data.secret)
  }, [loginData, loginExtData])

  /**
   * STEP 3: ME DATA
   * getMe is fired from App.js when the secret is saved in the state (context).
   * when me data comes:
   *  - we set i18n lang and invalidate queries to get them in user lang
   *  - add extra data to user and we save it in the state (context)
   */
  useEffect(() => {
    if (!meData || !isLogging) return

    const browserLang = navigator.language.split('-')[0]
    const userLang = meData.profile.lang || browserLang

    if (userLang !== window.localStorage.getItem('i18nextLng')) {
      i18n.changeLanguage(userLang)
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes('lang') })
    }

    // TODO: mutate react-query cache instead save it on localStorage
    const userData = {
      ...meData,
      isModerator: (loginData || loginExtData)?.isModerator,
      isSerieCreator: (loginData || loginExtData)?.seriesCreator,
      status: {
        value: meData.status,
        ...getStatuses(),
      }
    }

    setUser(userData)
  }, [meData])

  /**
   * STEP 4: USER STATUS - PRELOGGED vs LOGGED
   * When we have the user data, we check the status
   * in order to manage if the user should be pre-logged or fully logged
   */
  useEffect(() => {
    if (!user || !isLogging) return

    const { isDeleting, isDeleted, isBanned } = user.status

    if (isDeleting || isDeleted || isBanned) {
      logout()
      Snackbar.open({
        icon: ErrorSVG,
        message: t('notPossibleLogInThisAccount')
      })
      return
    }

    internalLogin()
  }, [user])

  useEffect(() => {
    if (loginError || loginExtError) {
      setIsLogging(false)
    }
  }, [loginError, loginExtError])

  useEffect(() => {
    if (isLogging && loginError) resetLogin()
    if (isLogging && loginExtError) resetLogin()
  }, [isLogging, loginError, loginExtError, resetLogin])


  const value = {
    isLogging,
    isPreLoggedIn,
    isLoggedIn,
    secret,
    login: handleLogin,
    loginExternal: handleLoginExternal,
    loginError: loginError || loginExtError,
    logout,
    user,
    loginWithSecret
  }

  return (
    <sessionContext.Provider value={value}>
      {initApp && children}
    </sessionContext.Provider>
  )
}

export default SessionProvider
